import { useCallback, useEffect, useRef, useState } from "react";
import { loadCoachSessionSettings } from "../utils/liveTrainingSettings";
import { appendLiveTrainingEvalDebug } from "../utils/liveTrainingEvalDebugLog";

export const COACH_TABLE_VIDEO_SOURCE = "coach_table_video";

function normalizeTableList(tableNumbers) {
  return [
    ...new Set(
      (tableNumbers || [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0)
    ),
  ].sort((a, b) => a - b);
}

/**
 * Coach-controlled start/stop for on-demand video from table cameras.
 * Supports independent per-table start/stop plus start-all / stop-all.
 * Relays via start/stop_table_video_recording with source=coach_table_video;
 * devices save the clip as an autoeval video.
 */
export function useCoachTableVideoRecording({
  socket,
  teId,
  tsId,
  coachUserId,
}) {
  const [status, setStatus] = useState("idle"); // idle | starting | recording | stopping | error
  const [error, setError] = useState(null);
  const [selectedTables, setSelectedTables] = useState(() => new Set());
  const [recordingTables, setRecordingTables] = useState(() => new Set());
  const [startingTables, setStartingTables] = useState(() => new Set());
  const [captureId, setCaptureId] = useState(null);
  /** @type {React.MutableRefObject<Map<number, "start"|"stop">>} */
  const pendingByTableRef = useRef(new Map());
  /** @type {React.MutableRefObject<Set<number>>} */
  const retriedPlayerFallbackRef = useRef(new Set());
  const recordingTablesRef = useRef(recordingTables);

  useEffect(() => {
    recordingTablesRef.current = recordingTables;
  }, [recordingTables]);

  const syncStartingTables = useCallback((pendingMap) => {
    const next = new Set();
    for (const [tn, act] of pendingMap.entries()) {
      if (act === "start") next.add(tn);
    }
    setStartingTables(next);
  }, []);

  const syncStatusFromRecording = useCallback((recordingSet, pendingMap) => {
    syncStartingTables(pendingMap);
    if (pendingMap.size > 0) {
      const actions = [...pendingMap.values()];
      if (actions.every((a) => a === "stop")) {
        setStatus("stopping");
      } else if (actions.every((a) => a === "start")) {
        setStatus(recordingSet.size > actions.length ? "recording" : "starting");
      } else {
        setStatus(recordingSet.size > 0 ? "recording" : "starting");
      }
      return;
    }
    if (recordingSet.size > 0) {
      setStatus("recording");
      return;
    }
    setStatus((prev) => (prev === "error" ? "error" : "idle"));
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const onAck = (ack = {}) => {
      const action = String(ack.action || "");
      const tn = Number(ack.table_number);
      const pending = pendingByTableRef.current;
      const pendingAction =
        Number.isFinite(tn) && tn > 0 ? pending.get(tn) : null;

      if (action === "start_video") {
        if (pendingAction !== "start" && pending.size === 0) return;
        if (Number.isFinite(tn) && tn > 0) pending.delete(tn);

        appendLiveTrainingEvalDebug("coach_start_ack", {
          ok: ack.ok !== false,
          code: ack.code || null,
          broadcast: ack.broadcast === true,
          delivered: ack.delivered ?? null,
          captureId: ack.capture_id || null,
          source: ack.source || null,
          table: tn || null,
        });

        if (ack.ok === false) {
          if (Number.isFinite(tn) && tn > 0) {
            setRecordingTables((prev) => {
              const next = new Set(prev);
              next.delete(tn);
              recordingTablesRef.current = next;
              syncStatusFromRecording(next, pending);
              return next;
            });
          }
          setError(
            ack.code === "CONSENT_REQUIRED"
              ? "Video consent required"
              : ack.code === "missing_fields"
                ? "Missing table or exercise"
                : ack.code || "Could not start recording"
          );
          if (pending.size === 0 && recordingTablesRef.current.size === 0) {
            setStatus("error");
          }
          return;
        }

        if (
          ack.broadcast !== true &&
          ack.delivered != null &&
          Number(ack.delivered) === 0
        ) {
          // If no dedicated table camera announced this table, fall back once
          // to seated player phones on that table (`coach_video` table relay).
          if (
            Number.isFinite(tn) &&
            tn > 0 &&
            ack.source === COACH_TABLE_VIDEO_SOURCE &&
            !retriedPlayerFallbackRef.current.has(tn) &&
            teId &&
            coachUserId
          ) {
            retriedPlayerFallbackRef.current.add(tn);
            pending.set(tn, "start");
            syncStatusFromRecording(recordingTablesRef.current, pending);
            const fps = loadCoachSessionSettings(tsId).recommendedRecordingFps;
            socket.emit("start_table_video_recording", {
              te_id: teId,
              ts_id: tsId,
              coach_id: coachUserId,
              table_number: tn,
              source: "coach_video",
              fps,
            });
            return;
          }
          if (Number.isFinite(tn) && tn > 0) {
            retriedPlayerFallbackRef.current.delete(tn);
            setRecordingTables((prev) => {
              const next = new Set(prev);
              next.delete(tn);
              recordingTablesRef.current = next;
              syncStatusFromRecording(next, pending);
              return next;
            });
          }
          setError(
            "No camera phone online for that table. Ask a player at the table to keep Live Training open with camera enabled."
          );
          if (pending.size === 0 && recordingTablesRef.current.size === 0) {
            setStatus("error");
          }
          return;
        }

        if (ack.capture_id) setCaptureId(ack.capture_id);
        setError(null);
        // Only mark recording after successful ack + delivery (not optimistic).
        if (Number.isFinite(tn) && tn > 0) {
          retriedPlayerFallbackRef.current.delete(tn);
          setRecordingTables((prev) => {
            const next = new Set(prev);
            next.add(tn);
            recordingTablesRef.current = next;
            syncStatusFromRecording(next, pending);
            return next;
          });
        } else {
          syncStatusFromRecording(recordingTablesRef.current, pending);
        }
        return;
      }

      if (action === "stop_video") {
        if (pendingAction !== "stop" && pending.size === 0) return;
        if (Number.isFinite(tn) && tn > 0) pending.delete(tn);

        if (ack.ok === false) {
          setError(ack.code || "Could not stop recording");
        }

        if (Number.isFinite(tn) && tn > 0) {
          setRecordingTables((prev) => {
            const next = new Set(prev);
            next.delete(tn);
            recordingTablesRef.current = next;
            if (next.size === 0) setCaptureId(null);
            syncStatusFromRecording(next, pending);
            return next;
          });
        } else if (pending.size === 0) {
          setCaptureId(null);
          setRecordingTables(new Set());
          recordingTablesRef.current = new Set();
          setStatus("idle");
        }
      }
    };

    socket.on("coachTableRecordingAck", onAck);
    return () => {
      socket.off("coachTableRecordingAck", onAck);
    };
  }, [socket, teId, tsId, coachUserId, syncStatusFromRecording]);

  useEffect(() => {
    pendingByTableRef.current = new Map();
    retriedPlayerFallbackRef.current = new Set();
    setStatus("idle");
    setError(null);
    setCaptureId(null);
    setSelectedTables(new Set());
    setRecordingTables(new Set());
    setStartingTables(new Set());
    recordingTablesRef.current = new Set();
  }, [teId]);

  const toggleTable = useCallback((tableNumber) => {
    const tn = Number(tableNumber);
    if (!Number.isFinite(tn) || tn <= 0) return;
    if (recordingTablesRef.current.has(tn)) return;
    setSelectedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tn)) next.delete(tn);
      else next.add(tn);
      return next;
    });
    setError(null);
  }, []);

  const selectAll = useCallback((tableNumbers = []) => {
    setSelectedTables(new Set(normalizeTableList(tableNumbers)));
    setError(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTables(new Set());
  }, []);

  const setTableNumber = useCallback((tn) => {
    const n = Number(tn);
    if (!Number.isFinite(n) || n <= 0) {
      setSelectedTables(new Set());
      return;
    }
    setSelectedTables(new Set([n]));
  }, []);

  const start = useCallback(
    (selectedTableNumber) => {
      const list =
        selectedTableNumber != null &&
        !Array.isArray(selectedTableNumber) &&
        Number.isFinite(Number(selectedTableNumber))
          ? normalizeTableList([selectedTableNumber])
          : normalizeTableList(
              Array.isArray(selectedTableNumber)
                ? selectedTableNumber
                : [...selectedTables]
            );

      const toStart = list.filter((tn) => !recordingTablesRef.current.has(tn));
      if (!socket || !teId || !coachUserId || toStart.length === 0) {
        if (list.length === 0) {
          setStatus("error");
          setError("Select at least one table to record");
        }
        return;
      }

      setSelectedTables((prev) => new Set([...prev, ...toStart]));
      setError(null);

      const pending = pendingByTableRef.current;
      for (const tn of toStart) pending.set(tn, "start");
      // Stay in "starting" until coachTableRecordingAck confirms delivery.
      syncStatusFromRecording(recordingTablesRef.current, pending);

      const fps = loadCoachSessionSettings(tsId).recommendedRecordingFps;
      appendLiveTrainingEvalDebug("coach_emit_start_tables", {
        tables: toStart,
        teId,
        fps,
      });
      for (const tn of toStart) {
        socket.emit("start_table_video_recording", {
          te_id: teId,
          ts_id: tsId,
          coach_id: coachUserId,
          table_number: tn,
          source: COACH_TABLE_VIDEO_SOURCE,
          fps,
        });
      }
    },
    [socket, teId, tsId, coachUserId, selectedTables, syncStatusFromRecording]
  );

  const stopTable = useCallback(
    (tableNumber) => {
      const tn = Number(tableNumber);
      if (!socket || !teId || !coachUserId) return;
      if (!Number.isFinite(tn) || tn <= 0) return;
      if (!recordingTablesRef.current.has(tn)) return;

      setError(null);
      const pending = pendingByTableRef.current;
      pending.set(tn, "stop");
      syncStatusFromRecording(recordingTablesRef.current, pending);

      appendLiveTrainingEvalDebug("coach_emit_stop_table", {
        table: tn,
        teId,
      });
      socket.emit("stop_table_video_recording", {
        te_id: teId,
        ts_id: tsId,
        coach_id: coachUserId,
        table_number: tn,
        source: COACH_TABLE_VIDEO_SOURCE,
      });
    },
    [socket, teId, tsId, coachUserId, syncStatusFromRecording]
  );

  const stop = useCallback(() => {
    const list = [...recordingTablesRef.current].sort((a, b) => a - b);
    if (!socket || !teId || !coachUserId || list.length === 0) return;

    setError(null);
    const pending = pendingByTableRef.current;
    for (const tn of list) pending.set(tn, "stop");
    syncStatusFromRecording(recordingTablesRef.current, pending);

    appendLiveTrainingEvalDebug("coach_emit_stop_tables", {
      tables: list,
      teId,
    });
    for (const tn of list) {
      socket.emit("stop_table_video_recording", {
        te_id: teId,
        ts_id: tsId,
        coach_id: coachUserId,
        table_number: tn,
        source: COACH_TABLE_VIDEO_SOURCE,
      });
    }
  }, [socket, teId, tsId, coachUserId, syncStatusFromRecording]);

  const resetError = useCallback(() => {
    if (status === "error") {
      setStatus(recordingTablesRef.current.size > 0 ? "recording" : "idle");
      setError(null);
    }
  }, [status]);

  const isTableSelected = useCallback(
    (tableNumber) => selectedTables.has(Number(tableNumber)),
    [selectedTables]
  );
  const isTableRecording = useCallback(
    (tableNumber) => recordingTables.has(Number(tableNumber)),
    [recordingTables]
  );
  const isTableStarting = useCallback(
    (tableNumber) => startingTables.has(Number(tableNumber)),
    [startingTables]
  );

  const tableNumber =
    recordingTables.size > 0
      ? [...recordingTables][0]
      : selectedTables.size > 0
        ? [...selectedTables][0]
        : null;

  return {
    status,
    error,
    tableNumber,
    selectedTables,
    recordingTables,
    startingTables,
    selectedCount: selectedTables.size,
    recordingCount: recordingTables.size,
    captureId,
    recording: recordingTables.size > 0 || status === "starting",
    start,
    stop,
    stopTable,
    toggleTable,
    selectAll,
    clearSelection,
    resetError,
    setTableNumber,
    isTableSelected,
    isTableRecording,
    isTableStarting,
  };
}
